/**
 * GlobalView — Train Detail Popup
 *
 * Overlay showing details for the selected train.
 * Appears when a train entity is selected, dismisses on close or ESC.
 * On mobile: renders as a bottom sheet with minimize/expand.
 */

import { useEffect, useRef } from 'react';
import { useGlobeStore } from '../../stores/globeStore';
import { useEntityStore } from '../../stores/entityStore';
import { getTrainCategoryLabel, getTrainCategoryColor } from '../../services/layers/trainIcons';
import { useDraggable } from '../../hooks/useDraggable';
import { useIsMobile } from '../../hooks/useIsMobile';
import EntityBottomSheet from './EntityBottomSheet';
import type { TrainProperties } from '@globalview/shared';
import styles from './TrainPopup.module.css';

// ─── Formatters ──────────────────────────────────────────────────

function fmtCoord(val: number, decimals = 5): string {
  return val.toFixed(decimals);
}

function timeAgo(isoStr: string): string {
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 5) return 'jetzt';
  if (diff < 60) return `vor ${Math.round(diff)}s`;
  return `vor ${Math.round(diff / 60)}min`;
}

// ─── Shared Content ──────────────────────────────────────────────

interface TrainContentProps {
  entity: { position: { lat: number; lon: number }; source: string; timestamp: string };
  props: TrainProperties;
}

function TrainContent({ entity, props }: TrainContentProps) {
  const category = props.category ?? '';
  const operator = props.operator ?? '—';
  const destination = props.destination ?? '—';
  const departureStation = props.departureStation ?? '—';
  const delay = props.delay ?? 0;
  const platform = props.platform;
  const nextStop = props.nextStop;

  // Delay badge
  let delayClass = styles.onTime;
  let delayText = 'PÜNKTLICH';
  if (delay > 0 && delay < 5) {
    delayClass = styles.delayed;
    delayText = `+${delay} min`;
  } else if (delay >= 5) {
    delayClass = styles.heavyDelay;
    delayText = `+${delay} min`;
  }

  return (
    <>
      {/* Delay badge */}
      <div className={`${styles.delayBadge} ${delayClass}`}>
        {delayText}
      </div>

      {/* Route */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>ROUTE</div>
        <div className={styles.grid}>
          <Row label="VON" value={departureStation} />
          <Row label="NACH" value={destination} />
          {nextStop && <Row label="NEXT" value={nextStop} />}
          {platform && <Row label="GL" value={platform} />}
        </div>
      </div>

      {/* Position */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>POSITION</div>
        <div className={styles.grid}>
          <Row label="LAT" value={`${fmtCoord(entity.position.lat)}°`} />
          <Row label="LON" value={`${fmtCoord(entity.position.lon)}°`} />
        </div>
      </div>

      {/* Info */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>INFO</div>
        <div className={styles.grid}>
          <Row label="TYP" value={getTrainCategoryLabel(category)} />
          <Row label="OP" value={operator} />
          <Row label="SRC" value={entity.source.toUpperCase()} />
          <Row label="UPD" value={timeAgo(entity.timestamp)} />
        </div>
      </div>
    </>
  );
}

// ─── Component ───────────────────────────────────────────────────

export default function TrainPopup() {
  const selectedEntityId = useGlobeStore((s) => s.selectedEntityId);
  const setSelectedEntity = useGlobeStore((s) => s.setSelectedEntity);
  const entity = useEntityStore((s) =>
    selectedEntityId ? s.entities.get(selectedEntityId) : undefined,
  );
  const isMobile = useIsMobile();

  const { style: dragStyle, onMouseDown, isDragging, reset } = useDraggable(296, 32);
  const prevEntityRef = useRef<string | null>(null);

  // Reset position when a new train is selected
  useEffect(() => {
    if (selectedEntityId && selectedEntityId !== prevEntityRef.current) {
      reset();
      prevEntityRef.current = selectedEntityId;
    }
  }, [selectedEntityId, reset]);

  // ESC key to close
  useEffect(() => {
    if (!selectedEntityId) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedEntity(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEntityId, setSelectedEntity]);

  // Only render for train entities
  if (!selectedEntityId || !entity || entity.type !== 'train') return null;

  const props = entity.properties as unknown as TrainProperties;
  const trainName = props.trainName ?? '—';
  const category = props.category ?? '';
  const number = props.number ?? '';
  const destination = props.destination ?? '—';
  const categoryColor = getTrainCategoryColor(category);

  const handleClose = () => {
    setSelectedEntity(null);
  };

  // Desktop popup
  const desktopPopup = (
    <div
      className={styles.popup}
      style={dragStyle}
      id="train-popup"
      data-draggable
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Header — drag handle */}
      <div
        className={styles.header}
        onMouseDown={onMouseDown}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div className={styles.headerLeft}>
          <span className={styles.trainName} style={{ color: categoryColor }}>
            {trainName}
          </span>
          <span className={styles.category}>
            {number ? `Nr. ${number}` : getTrainCategoryLabel(category)}
          </span>
        </div>
        <button
          className={styles.closeBtn}
          onClick={handleClose}
          title="Schliessen (ESC)"
          id="train-popup-close"
        >
          ✕
        </button>
      </div>
      <TrainContent entity={entity} props={props} />
    </div>
  );

  // Mobile bottom sheet wrapping
  if (isMobile) {
    return (
      <EntityBottomSheet
        visible={true}
        entityName={trainName}
        entitySub={`→ ${destination}`}
        icon="🚂"
        onClose={handleClose}
        desktopPopup={desktopPopup}
      >
        <TrainContent entity={entity} props={props} />
      </EntityBottomSheet>
    );
  }

  return desktopPopup;
}

// ─── Row Helper ──────────────────────────────────────────────────

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>{value}</span>
    </div>
  );
}
